import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, User, Mail, Shield, Layers, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const { user } = useAuth();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Please fill all password fields");
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success("Password changed successfully!");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your account settings
        </p>
      </div>

      {/* Profile Info */}
      <div className="stat-card">
        <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Profile Information
        </h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-prepify-100 flex items-center justify-center">
              <User size={24} className="text-prepify-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{user?.name}</p>
              <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-slate-600">
            <Mail size={18} className="text-slate-400" />
            <span>{user?.email}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-600">
            <Shield size={18} className="text-slate-400" />
            <span className="capitalize">{user?.role} Access</span>
          </div>
        </div>
      </div>

      {/* Admin / CM-only: link to Section Templates */}
      {(user?.role === "admin" || user?.role === "content_manager") && (
        <div className="stat-card">
          <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Task Configuration
          </h2>
          <Link
            to="/app/settings/section-templates"
            className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-prepify-300 hover:bg-prepify-50/40 transition-colors"
            data-testid="section-templates-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-prepify-100 flex items-center justify-center">
                <Layers size={18} className="text-prepify-600" />
              </div>
              <div>
                <div className="font-medium text-slate-900">Section Templates</div>
                <div className="text-sm text-slate-500">Configure Mock & Magazine sections per exam</div>
              </div>
            </div>
            <ChevronRight size={18} className="text-slate-400" />
          </Link>
        </div>
      )}

      {/* Change Password */}
      <div className="stat-card">
        <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Change Password
        </h2>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div className="space-y-2">
            <Label>Current Password</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? "text" : "password"}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Enter current password"
                className="pr-10"
                data-testid="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>New Password</Label>
            <div className="relative">
              <Input
                type={showNewPassword ? "text" : "password"}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
                className="pr-10"
                data-testid="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Confirm New Password</Label>
            <Input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              placeholder="Confirm new password"
              data-testid="confirm-password"
            />
          </div>
          
          <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="change-password-btn">
            {loading ? (
              <div className="spinner" style={{ width: 16, height: 16, borderTopColor: 'white' }}></div>
            ) : (
              <>
                <Lock size={16} className="mr-2" />
                Change Password
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Settings;
